import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getUserFavorites } from '@/redux/selectors/favorites';
import {
  getRentalsFetched,
  getRentalsList,
  getRentalsTiles,
  getSimilarRentalsFetched,
  getSimilarRentalsList,
  getSimilarRentalsTiles,
} from '@/redux/selectors/listing/similarRentals';
import { EListingSource } from '@/services/analytics/listings/types';
import { mapAutoRentalsToTiles } from '@/utility/mapAutoSearchResultToTile';

import RentalsCarousel from './RentalsCarousel';

type TSimilarRentalsCarouselProps = {
  maxCols?: number;
  maxRentals?: number;
  verticalScrollGrid?: boolean;
  inline?: boolean;
  title?: string;
  useRentalsFromId?: boolean;
  extraQueryParams?: string;
  eventSource?: EListingSource;
  isRow?: boolean;
  autoCssJudgeStyle?: boolean;
  isAuto?: boolean;
};

const SimilarRentalsCarousel = ({ isAuto = false, ...props }: TSimilarRentalsCarouselProps) => {
  if (isAuto) return <AutoSimilarRentalsCarousel {...props} />;
  return <DefaultSimilarRentalsCarousel {...props} />;
};

export default SimilarRentalsCarousel;
const AutoSimilarRentalsCarousel = ({
  title,
  maxCols,
  maxRentals,
  verticalScrollGrid = false,
  inline = false,
  useRentalsFromId = false,
  extraQueryParams,
  eventSource,
  isRow = false,
  autoCssJudgeStyle = false,
}: Omit<TSimilarRentalsCarouselProps, 'isAuto'>) => {
  const favorites = useSelector(getUserFavorites);
  const rentalsData = useSelector(useRentalsFromId ? getSimilarRentalsList : getRentalsList);
  const isFetched = useSelector(useRentalsFromId ? getSimilarRentalsFetched : getRentalsFetched);

  const autoRentalsList = useMemo(() => {
    return mapAutoRentalsToTiles({ rentals: rentalsData, favorites: favorites?.rentals });
  }, [rentalsData, favorites?.rentals]);

  return (
    <RentalsCarousel
      title={title}
      maxCols={maxCols}
      maxRentals={maxRentals}
      verticalScrollGrid={verticalScrollGrid}
      inline={inline}
      rentalsList={autoRentalsList}
      loading={!isFetched}
      extraQueryParams={extraQueryParams}
      eventSource={eventSource}
      isRow={isRow}
      autoCssJudgeStyle={autoCssJudgeStyle}
      isAuto
    />
  );
};

const DefaultSimilarRentalsCarousel = ({
  title,
  maxCols,
  maxRentals,
  verticalScrollGrid = false,
  inline = false,
  useRentalsFromId = false,
  extraQueryParams,
  eventSource,
  isRow = false,
  autoCssJudgeStyle = false,
}: Omit<TSimilarRentalsCarouselProps, 'isAuto'>) => {
  const rentalsList = useSelector(useRentalsFromId ? getSimilarRentalsTiles : getRentalsTiles);
  const isFetched = useSelector(useRentalsFromId ? getSimilarRentalsFetched : getRentalsFetched);

  return (
    <RentalsCarousel
      title={title}
      maxCols={maxCols}
      maxRentals={maxRentals}
      verticalScrollGrid={verticalScrollGrid}
      inline={inline}
      rentalsList={rentalsList}
      loading={!isFetched}
      extraQueryParams={extraQueryParams}
      eventSource={eventSource}
      isRow={isRow}
      autoCssJudgeStyle={autoCssJudgeStyle}
    />
  );
};
